import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

interface City {
  nom: string;
  code: string;
  departement: {
    nom: string;
    code: string;
  };
}

const fetchCities = async (searchTerm: string): Promise<City[]> => {
  if (!searchTerm || searchTerm.length < 2) return [];

  const response = await fetch(
    `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(
      searchTerm
    )}&fields=departement&boost=population&limit=5`
  );

  if (!response.ok) {
    throw new Error("Failed to fetch cities");
  }

  return response.json();
};

export function useCityName(initialValue?: string) {
  const [query, setQuery] = useState(initialValue ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: cities = [], isLoading } = useQuery({
    queryKey: ["cities", query],
    queryFn: () => fetchCities(query),
    enabled: query.length >= 2,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  const updateInputValue = (value: string) => {
    setQuery(value);
    setSelectedIndex(0);
    setIsOpen(value.length >= 2);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || cities.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < cities.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : cities.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          selectCity(cities[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const selectCity = (city: City) => {
    setQuery(city.nom);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return {
    query,
    setQuery: updateInputValue,
    cities,
    isOpen: isOpen && cities.length > 0,
    isLoading,
    selectedIndex,
    containerRef,
    dropdownRef,
    handleKeyDown,
    selectCity,
  };
}
